import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import { FaSyringe, FaArrowLeft } from "react-icons/fa";
import { DataGridTable, StatCard } from "../components";
import "../styles/common.css";
import "./DetailSante.css";

export default function DetailSante() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sante, setSante] = useState(null);
  const [animaux, setAnimaux] = useState([]);
  const [troupeaux, setTroupeaux] = useState([]);
  const [stocksDisponibles, setStocksDisponibles] = useState([]);
  const [produitsUtilises, setProduitsUtilises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);



  useEffect(() => {
    const fetchSante = async () => {
      try {
        const response = await api.get(`/api/sante/${id}/`);
        setSante(response.data);
        
        // Charger les produits utilisés depuis le champ 'utilisations' de l'API
        if (response.data.utilisations && Array.isArray(response.data.utilisations)) {
          const produits = response.data.utilisations.map(u => ({
            stock_id: u.stock || u.stock_id || "",
            quantite_utilisee: u.quantite_utilisee || "",
            notes: u.notes || "",
            produit_nom: u.produit_nom || "",
            produit_unite: u.produit_unite || "",
          }));
          setProduitsUtilises(produits);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement de la fiche de santé:", err);
        let errorMessage = "Impossible de charger la fiche de santé.";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "La fiche de santé demandée n'existe pas.";
          } else if (err.response.status === 403) {
            errorMessage = "Vous n'avez pas la permission d'accéder à cette fiche de santé.";
          } else if (err.response.status >= 500) {
            errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
          } else {
            errorMessage = err.response.data?.detail || err.response.data?.message || errorMessage;
          }
        }
        setError(errorMessage);
        setLoading(false);
      }
    };

    if (id) {
      fetchSante();
    } else {
      setError("ID de la fiche de santé manquant.");
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [animauxRes, troupeauxRes, stocksRes] = await Promise.all([
          api.get("/api/animaux/"),
          api.get("/api/troupeaux/"),
          api.get("/api/stocks-pharmacie/"), // Charger TOUS les stocks, pas seulement les disponibles
        ]);
        setAnimaux(animauxRes.data);
        setTroupeaux(troupeauxRes.data);
        setStocksDisponibles(stocksRes.data);
      } catch (err) {
        console.error("Erreur lors du chargement des données:", err);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const getAnimalMatricule = (animalId) => {
    if (!animalId) return "Non spécifié";
    const animal = animaux.find(a => a.id === animalId);
    return animal ? animal.matricule : "Non spécifié";
  };

  const getTroupeauName = (animalId) => {
    if (!animalId) return "Non spécifié";
    const animal = animaux.find(a => a.id === animalId);
    if (!animal) return "Non spécifié";
    
    // Si troupeau est un objet avec nom
    if (typeof animal.troupeau === 'object' && animal.troupeau?.nom) {
      return animal.troupeau.nom;
    }
    // Si troupeau est un ID (nombre), chercher dans la liste des troupeaux
    if (typeof animal.troupeau === 'number') {
      const troupeau = troupeaux.find(t => t.id === animal.troupeau);
      if (troupeau) {
        return troupeau.nom || "Non spécifié";
      }
    }
    return "Non spécifié";
  };

  const getProduitLabel = (produitUtilise) => {
    // Utiliser produit_nom directement depuis l'API si disponible
    if (produitUtilise.produit_nom) {
      return produitUtilise.produit_nom;
    }
    // Sinon, chercher dans les stocks disponibles
    if (produitUtilise.stock_id) {
      const stock = stocksDisponibles.find(s => s.id === parseInt(produitUtilise.stock_id));
      if (stock) {
        return stock.produit_nom || stock.produit?.nom || "Produit inconnu";
      }
    }
    return "Non spécifié";
  };

  const getProduitUnite = (produitUtilise) => {
    // Utiliser produit_unite directement depuis l'API si disponible
    if (produitUtilise.produit_unite) {
      return produitUtilise.produit_unite;
    }
    // Sinon, chercher dans les stocks disponibles
    if (produitUtilise.stock_id) {
      const stock = stocksDisponibles.find(s => s.id === parseInt(produitUtilise.stock_id));
      return stock ? (stock.produit_unite || stock.produit?.unite || "") : "";
    }
    return "";
  };

  const getTypeInterventionLabel = (type) => {
    const mapping = {
      'vaccination': 'Vaccination',
      'traitement': 'Traitement',
      'vermifuge': 'Vermifuge',
      'autre': 'Autre',
    };
    return mapping[type] || type;
  };

  const getStatutLabel = (statut) => {
    return statut === 'traite' ? 'Traité' : 'Non Traité';
  };

  // Configuration des colonnes pour DataGrid
  const columns = useMemo(() => [
    { 
      field: "produit", 
      headerName: "Produit", 
      flex: 1, 
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
    { 
      field: "quantite", 
      headerName: "Quantité utilisée", 
      flex: 1, 
      minWidth: 180,
      headerAlign: 'right',
      align: 'right',
    },
    { 
      field: "notes", 
      headerName: "Notes", 
      flex: 1, 
      minWidth: 200,
      headerAlign: 'left',
      align: 'left',
    },
  ], []);

  // Préparer les données pour DataGrid
  const rows = useMemo(() => {
    return produitsUtilises.map((produit, index) => ({
      id: index,
      produit: getProduitLabel(produit),
      quantite: `${produit.quantite_utilisee || "0"} ${getProduitUnite(produit)}`,
      notes: produit.notes || "N/A",
    }));
  }, [produitsUtilises, stocksDisponibles]);

  if (loading) {
    return (
      <div className="sante-detail-page">
        <div className="loading-container">
          <p>Chargement de la fiche de santé...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sante-detail-page">
        <div className="title-badge">
          <FaSyringe className="page-title-icon" aria-hidden="true" />
          <span>{t.error}</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <div className="actions">
            <button
              type="button"
              onClick={() => navigate("/sante")}
              className="cancel-but"
            >
              <FaArrowLeft />
              <span>{t.backToList}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sante-detail-page">
      <div className="title-badge">
        <FaSyringe className="page-title-icon" aria-hidden="true" />
        <span>{t.healthDetail}</span>
      </div>

      <div className="actions">
        <button
          type="button"
          onClick={() => navigate("/sante")}
          className="cancel-but"
        >
          <FaArrowLeft />
          <span>Retour à la liste</span>
        </button>
      </div>

      {sante && (
        <div className="sante-detail-container">
          {/* Section des cartes d'informations */}
          <h2 className="dashboard-section-title">Informations de la fiche de santé</h2>
          <div className="kpi-grid">
            <StatCard 
              type="troupeau" 
              label="TROUPEAU" 
              value={getTroupeauName(sante.animal)} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="animal" 
              label="ANIMAL" 
              value={getAnimalMatricule(sante.animal)} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="type-intervention" 
              label="TYPE D'INTERVENTION" 
              value={getTypeInterventionLabel(sante.type_intervention)} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="statut" 
              label="STATUT" 
              value={getStatutLabel(sante.statut)} 
              variant="dashboard" 
              showIcon={false} 
            />
            <StatCard 
              type="date" 
              label="DATE" 
              value={formatDate(sante.date)} 
              variant="dashboard" 
              showIcon={false} 
            />
            {sante.prochaine_intervention && (
              <StatCard 
                type="prochaine-intervention" 
                label="PROCHAINE INTERVENTION" 
                value={formatDate(sante.prochaine_intervention)} 
                variant="dashboard" 
                showIcon={false} 
              />
            )}
            {produitsUtilises.length > 0 && (
              <StatCard 
                type="produits-count" 
                label="PRODUITS UTILISÉS" 
                value={produitsUtilises.length} 
                variant="dashboard" 
                showIcon={false} 
              />
            )}
          </div>

          {/* Description si présente */}
          {sante.description && (
            <div className="description-section">
              <h2 className="dashboard-section-title">Description</h2>
              <div className="description-card">
                <p>{sante.description}</p>
              </div>
            </div>
          )}

          {/* Liste des produits utilisés */}
          {produitsUtilises.length > 0 && (
            <>
              <h2 className="dashboard-section-title">Produits utilisés</h2>
              {/* DataGrid */}
              <DataGridTable
                rows={rows}
                columns={columns}
                loading={loading}
                storageKey="detail-sante-zoom-level"
                initialSort={{ field: 'produit', sort: 'asc' }}
                pageSize={25}
                containerClassName="produits-grid-container"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
